<template>
  <div class="flex items-center relative" ref="container"
    :class="[{ 'flex-row-reverse': position === 'right' }, disabled ? 'toggle-disabled' : '']" @click="updateValue">
    <PressedIndicator v-if="showIndicator" id="pressed-indicator" ref="indicator"
      color="secondary" />
    <input class="hidden" type="checkbox" :name="name" :checked="modelValue"
      @change="updateValue" />
    <div
      class="w-13 h-8 rounded-3xl shrink-0 flex items-center align-middle select-none transition-all duration-300 ease-in-out toggle-track"
      :class="[
        disabled ? (modelValue ? 'toggle-track--disabled-on' : 'toggle-track--disabled-off') : (modelValue ? 'toggle-track--on' : 'toggle-track--off'),
        position === 'right' ? 'ml-4' : 'mr-4',
        !disabled && 'cursor-pointer'
      ]">
      <!-- Moving dot -->
      <div
        class="rounded-full bg-white absolute items-center transition-all duration-300 ease-in-out toggle-dot"
        :class="[
          modelValue ? 'translate-x-6 w-6 h-6' : 'translate-x-1 w-4 h-4',
          disabled ? (modelValue ? 'toggle-dot--disabled-on' : 'toggle-dot--disabled-off') : (modelValue ? 'toggle-dot--on' : 'toggle-dot--off')
        ]">
      </div>
    </div>
    <div class="flex flex-col flex-1 min-w-0" :class="disabled ? '' : 'cursor-pointer'">
      <span class="text-[13px] font-semibold" :class="disabled ? 'text-muted-foreground' : 'text-foreground'">{{ label }}</span>
      <span v-if="description" class="text-[12px] text-muted-foreground leading-snug mt-0.5">{{ description }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { InputTogglePosition } from '../../../GoManager';
import PressedIndicator from '../../PressedIndicator.vue';
import eventBus from '../../../eventbus';

const container = ref<HTMLElement | null>(null);
const indicator = ref<HTMLElement | null>(null);

const props = withDefaults(defineProps<{
  name: string;
  modelValue: boolean;
  label?: string;
  description?: string;
  showIndicator?: boolean;
  position?: InputTogglePosition;
  disabled?: boolean;
}>(), {
  position: 'left',
  indicator: false,
  disabled: false
});

const emit = defineEmits(['update:modelValue']);

const updateValue = (e: any) => {
  if (props.disabled) {
    // Shake feedback on tap
    container.value?.classList.remove('toggle-shake');
    void container.value?.offsetWidth; // force reflow
    container.value?.classList.add('toggle-shake');
    return;
  }

  eventBus['pressed-indicator'] = {
    container: container.value,
    event: e
  }

  emit('update:modelValue', !props.modelValue);
};
</script>

<style scoped>
/* ── Light mode ── */
.toggle-track--on {
  background: linear-gradient(135deg, #06C8FF 0%, #009FE3 100%);
  border: 2px solid rgba(6, 200, 255, 0.40);
  box-shadow: 0 2px 8px rgba(6, 200, 255, 0.25);
}
.toggle-track--off {
  background: var(--muted, #e2e8f0);
  border: 2px solid var(--border, rgba(0, 0, 0, 0.08));
}
.toggle-dot--on {
  border: 1px solid rgba(6, 200, 255, 0.20);
  box-shadow: 0 1px 4px rgba(6, 200, 255, 0.15);
}
.toggle-dot--off {
  border: 1px solid var(--border, rgba(0, 0, 0, 0.08));
}

/* ── Disabled state: ON (desaturated cyan, clearly muted) ── */
.toggle-track--disabled-on {
  background: linear-gradient(135deg, #7BBDCF 0%, #6AAAB8 100%);
  border: 2px solid rgba(6, 200, 255, 0.15);
  box-shadow: none;
  opacity: 0.6;
}
.toggle-dot--disabled-on {
  border: 1px solid rgba(6, 200, 255, 0.10);
  opacity: 0.75;
}
/* ── Disabled state: OFF ── */
.toggle-track--disabled-off {
  background: var(--muted, #e2e8f0);
  border: 2px solid var(--border, rgba(0, 0, 0, 0.06));
  opacity: 0.5;
}
.toggle-dot--disabled-off {
  border: 1px solid var(--border, rgba(0, 0, 0, 0.06));
  opacity: 0.6;
}

/* ── Shake animation ── */
.toggle-shake {
  animation: toggleShake 0.4s ease-in-out;
}
@keyframes toggleShake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
}

/* ── Dark mode ── */
:is(.dark *).toggle-track--on {
  box-shadow: 0 2px 8px rgba(6, 200, 255, 0.15);
  border: 2px solid rgba(6, 200, 255, 0.30);
}
:is(.dark *).toggle-track--off {
  background: rgba(255, 255, 255, 0.10);
  border: 2px solid rgba(255, 255, 255, 0.10);
}
:is(.dark *).toggle-dot--on {
  border: 1px solid rgba(6, 200, 255, 0.15);
}
:is(.dark *).toggle-dot--off {
  border: 1px solid rgba(255, 255, 255, 0.12);
}
:is(.dark *).toggle-track--disabled-on {
  background: linear-gradient(135deg, #4A8A9A 0%, #3D7585 100%);
  box-shadow: none;
  border: 2px solid rgba(6, 200, 255, 0.12);
  opacity: 0.55;
}
:is(.dark *).toggle-dot--disabled-on {
  border: 1px solid rgba(6, 200, 255, 0.08);
  opacity: 0.75;
}
:is(.dark *).toggle-track--disabled-off {
  background: rgba(255, 255, 255, 0.06);
  border: 2px solid rgba(255, 255, 255, 0.06);
  opacity: 0.5;
}
:is(.dark *).toggle-dot--disabled-off {
  border: 1px solid rgba(255, 255, 255, 0.08);
  opacity: 0.6;
}
</style>
